/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Info, OpenInNew, Visibility } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import DigitalSensorValue from '../../../DigitalSensorValue';
import AssetIcon from '../../../shared/atoms/AssetIcon';
import formatLatLng from '../../../../utils/formatLatLng';
import CustomTooltip from '../../../shared/atoms/CustomTooltip';
import AddressIconButton from '../../../shared/molecules/AddressIconButton';
import CopyCoordinatesIconButton from '../../../shared/molecules/CopyCoordinatesIconButton';
import { featurePropTypes } from '../../../ActuatorControlPanel/types';
import { nonActuatorSensorFilter } from '../../../../utils/actuatorUtils';
import { useIsTabletOrMobile } from '../../../../hooks/useIsTabletOrMobile';
import displayUnit from '../../../../utils/displayUnit';
import ColorReading from '../../../ColorReading';
import ActuatorIconWithStatus from '../../../ActuatorIconWithStatus';
import { getEnumReadingString } from '../../../Facilities/CenterPane/SensorReadings/components/SensorDisplay/utils';

const getSensorValue = (sensor, i18n) => {
  const measureUnitValue = sensor.unit;

  if (
    ((sensor.reading === null || sensor.reading === undefined) &&
      sensor.measuring_mode !== 'positioning') ||
    ((sensor.position === null || sensor.position === undefined) &&
      sensor.measuring_mode === 'positioning')
  )
    return '-';

  if (sensor.measuring_mode === 'digital')
    return (
      <DigitalSensorValue
        binarySensor={sensor.threshold?.[sensor.reading]}
        sensorId={sensor.id}
        colorized
      />
    );

  if (
    sensor.measuring_mode === 'positioning' &&
    sensor.position.lat &&
    sensor.position.lng
  )
    return formatLatLng(sensor.position.lat, sensor.position.lng);

  if (sensor.measuring_mode === 'color')
    return <ColorReading value={sensor.reading} width="2rem" height="2rem" />;

  if (sensor.measuring_mode === 'enum')
    return getEnumReadingString(sensor.reading, true);

  return (
    sensor.reading.toLocaleString(i18n.language) + displayUnit(measureUnitValue)
  );
};

const renderCorGPSInfo = (sensor, t) => {
  return (
    <CustomTooltip
      title={
        <Stack>
          {sensor.data.estBattPct && (
            <Typography>
              {t('machines.facilities.center_pane.sensor.battery_status', {
                percentage: Math.floor(sensor.data.estBattPct),
              })}
            </Typography>
          )}
          {sensor.data.lsrc && (
            <Typography>
              {t('machines.facilities.center_pane.sensor.collection_mode', {
                mode: sensor.data.lsrc,
              })}
            </Typography>
          )}
          {sensor.data.hacc && (
            <Typography>
              {t('machines.facilities.center_pane.sensor.horizontal_accuracy', {
                hacc: sensor.data.hacc,
              })}
            </Typography>
          )}
          <Stack direction="row" justifyContent="center">
            <CopyCoordinatesIconButton
              lat={sensor.data.lat}
              lng={sensor.data.lng}
              placement="bottom"
            />
            {sensor.data.address && (
              <AddressIconButton
                address={sensor.data.address}
                placement="bottom"
              />
            )}
          </Stack>
        </Stack>
      }
    >
      <Info />
    </CustomTooltip>
  );
};

const sortByNameOrId = (a, b) =>
  a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

const AssetReadingsList = ({ sensors, actuators, onAction }) => {
  const { i18n, t } = useTranslation();
  const isTabletOrMobile = useIsTabletOrMobile();
  const params = useParams();

  const sortedSensors = useMemo(
    () => (sensors ?? []).filter(nonActuatorSensorFilter).sort(sortByNameOrId),
    [sensors],
  );

  const sortedActuators = useMemo(
    () => (actuators ?? []).sort(sortByNameOrId),
    [actuators],
  );

  const goToActuatorDetails = actuator => {
    onAction(actuator.asset, false, `/actuators/${actuator.id}`);
  };

  const openActuatorPopUp = actuator =>
    window.map.addPopup(
      { type: 'ACTUATOR', ...actuator },
      params.location
        .split(';')[0]
        .split(',')
        .map(l => Number.parseFloat(l))
        .reverse(),
    );

  return (
    <Box
      sx={{
        fontSize: '12px',
        padding: '1rem',
        paddingTop: '10px',
        maxHeight: isTabletOrMobile ? '90px' : 'unset',
        paddingRight: isTabletOrMobile ? '1.5rem' : '1rem',
        overflowY: 'auto',
      }}
    >
      {sortedSensors.map(sensor => {
        return (
          <Stack
            key={sensor.id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AssetIcon
                status={sensor.status}
                measuringMode={sensor.measuring_mode}
              />
              <Typography textAlign="left">{sensor.name}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography textAlign="right" component="div">
                {getSensorValue(sensor, i18n)}
              </Typography>
              {(sensor.data?.estBattPct ||
                sensor.data?.lsrc ||
                sensor.data?.hacc ||
                sensor.data?.address) &&
                renderCorGPSInfo(sensor, t)}
            </Stack>
          </Stack>
        );
      })}
      {sortedActuators.map(actuator => (
        <Stack
          key={actuator.id}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ActuatorIconWithStatus
              type="library"
              name={actuator.icon}
              actuator={actuator}
            />
            <Typography>{actuator.name}</Typography>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <CustomTooltip title={t('components.pop_up.view_details')}>
              <IconButton
                size="small"
                sx={{
                  color: 'inherit',
                  padding: 0,
                }}
                onClick={() => goToActuatorDetails(actuator)}
              >
                <Visibility />
              </IconButton>
            </CustomTooltip>
            <CustomTooltip title={t('components.pop_up.open_pop-up')}>
              <IconButton
                size="small"
                sx={{
                  color: 'inherit',
                  padding: 0,
                }}
                onClick={() => openActuatorPopUp(actuator)}
              >
                <OpenInNew />
              </IconButton>
            </CustomTooltip>
          </Stack>
        </Stack>
      ))}
    </Box>
  );
};

AssetReadingsList.defaultProps = {
  sensors: [],
  actuators: [],
};

AssetReadingsList.propTypes = {
  sensors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      measuring_mode: PropTypes.oneOf([
        'digital',
        'positioning',
        'threshold',
        'no_threshold',
        'streaming',
        'enum',
        'color',
      ]).isRequired,
      reading: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit: PropTypes.string,
      threshold: PropTypes.object,
      position: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      data: PropTypes.object,
      device_id: PropTypes.string,
    }),
  ),
  actuators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      exposes: PropTypes.arrayOf(
        PropTypes.shape({ feature: featurePropTypes }),
      ),
    }),
  ),
  onAction: PropTypes.func.isRequired,
};

export default AssetReadingsList;
